import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { comentarioSolicitacaoService } from '../../services/comentarioSolicitacaoService';

export const SI_COMENTARIOS_QUERY_KEYS = {
  all: ['si', 'comentarios'] as const,
  porSolicitacao: (solicitacaoId: string) => [...SI_COMENTARIOS_QUERY_KEYS.all, solicitacaoId] as const,
};

export function useComentariosSolicitacaoQuery(solicitacaoId: string | undefined) {
  return useQuery({
    queryKey: SI_COMENTARIOS_QUERY_KEYS.porSolicitacao(solicitacaoId ?? ''),
    queryFn: () => comentarioSolicitacaoService.getBySolicitacao(solicitacaoId as string),
    enabled: Boolean(solicitacaoId),
  });
}

export function useComentarioSolicitacaoMutations(solicitacaoId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();

  const invalidar = () =>
    queryClient.invalidateQueries({ queryKey: SI_COMENTARIOS_QUERY_KEYS.porSolicitacao(solicitacaoId) });

  const erro = (acao: string) => (error: any) =>
    toast.error(`Erro ao ${acao} observação: ${error?.message || 'erro desconhecido'}`);

  const postar = useMutation({
    mutationFn: (texto: string) => {
      if (!user?.email) throw new Error('Usuário não identificado.');
      return comentarioSolicitacaoService.create(
        solicitacaoId,
        texto,
        { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email, avatar: user.photoURL ?? null },
        user.email
      );
    },
    onSuccess: invalidar,
    onError: erro('postar'),
  });

  const editar = useMutation({
    mutationFn: ({ comentarioId, texto }: { comentarioId: string; texto: string }) =>
      comentarioSolicitacaoService.update(solicitacaoId, comentarioId, texto),
    onSuccess: invalidar,
    onError: erro('editar'),
  });

  const excluir = useMutation({
    mutationFn: (comentarioId: string) => comentarioSolicitacaoService.delete(solicitacaoId, comentarioId),
    onSuccess: invalidar,
    onError: erro('excluir'),
  });

  return { postar, editar, excluir };
}
